import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'

import {
  getPromotionTypeAPI,
  getReimbursementMethodsAPI,
  getCampaignClusterAPI,
  getVendorListAPI,
  getVendorDataAPI,
  postCreateSKPAPI,
  postCopySKPAPI,
  putUpdateSKPAPI,
  getSKPDetailAPI,
  getSKPVendorDiffAPI,
  postUploadSKPDetailFileAPI,
  getDownloadSKPDetailsAPI,
  getBrandSubmissionDetailAPI,
  getBrandSubmissionsListAPI,
  getDeckPromotionFile,
  deleteSKPAPI,
} from 'features/Enterprise/SKP/utils/apiList'
import { getAllBrandV2 } from 'features/Product/services/brand'

import {
  PostCreateSKPParamInterface,
  PostCreateSKPCopyParamInterface,
  PutUpdateSKPInterface,
} from 'features/Enterprise/SKP/@types/sourcingTypes'

import { downloadFileUrl, downloadFileBlob } from 'utils/downloadFileUrl'

import { toastFailed, toastSuccess } from 'utils/toast'

import { AxiosError } from 'axios'
import {
  setPromotionTypes,
  setReimbursementMethod,
  setCampaignCluster,
  setSKPDetails,
  setIsLoading,
  setSKPCreateList,
  setVendorChangedForm,
  setShowVendorChangedModal,
  setEditDetail,
  setIsVendorDataEmpty,
  setBrandSubmissionsData,
  setBrandSubmissionList,
  setBrandV2Options,
} from './slice'

const SLICE_NAME = 'skpAddNew'

export const fetchPromotionType = createAsyncThunk(
  `${SLICE_NAME}/fetchPromotionType`,
  async (_, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getPromotionTypeAPI()

      dispatch(setPromotionTypes(response.data.datas))
      dispatch(setIsLoading(false))
      return true
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const fetchReimbursementMethod = createAsyncThunk(
  `${SLICE_NAME}/fetchReimbursementMethod`,
  async (promoTypeId: string, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getReimbursementMethodsAPI({ promotion_type_id: promoTypeId })
      dispatch(setReimbursementMethod(response.data.datas))
      dispatch(setIsLoading(false))
      return { isEmpty: response.data.datas.length === 0 }
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const fetchCampaignCluster = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignCluster`,
  async (_, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getCampaignClusterAPI()
      dispatch(setCampaignCluster(response.data.data.campaign_clusters))
      dispatch(setIsLoading(false))
      return true
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const fetchVendorList = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorList`,
  async (company_name: string) => {
    try {
      const response = await getVendorListAPI({ company_name })
      return response.data.datas
    } catch (error) {
      callErrorMsg(error)
      return []
    }
  },
)

export const fetchVendorDataSKP = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDataSKP`,
  async (vendor_id: number, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const response = await getVendorDataAPI(vendor_id)
      const { error } = response.data
      dispatch(setIsLoading(false))
      if (!error.status) {
        dispatch(setIsVendorDataEmpty(false))
      }
      return response.data
    } catch (error) {
      dispatch(setIsLoading(false))
      const { response } = error as AxiosError
      const data = response?.data as { error: { message: string } }
      const message = data.error.message || ''
      // nanti diganti pakai enum VENDOR_SKP_INFO
      if (message && message.length > 0 && message.includes('[VENDOR_SKP_INFO]')) {
        dispatch(setIsVendorDataEmpty(true))
      } else {
        callErrorMsg(error)
      }
      return undefined
    }
  },
)

export const postCreateNewSKP = createAsyncThunk(
  `${SLICE_NAME}/postCreateNewSKP`,
  async (params: PostCreateSKPParamInterface, { dispatch, getState }) => {
    dispatch(setIsLoading(true))

    const {
      skpAddNew: { skpCreateList },
    } = getState() as StoreStateType

    try {
      const response = await postCreateSKPAPI(params)

      const tempSKPLatest = skpCreateList[skpCreateList.length - 1]
      const tempSKPLIst = [...skpCreateList]

      const newSKP = {
        skpId: Number(response.data.skp_id),
      }

      if (tempSKPLatest.skpId === 0) {
        tempSKPLIst.pop()
        tempSKPLIst.push(newSKP)
      }

      dispatch(setSKPCreateList(tempSKPLIst))

      dispatch(setIsLoading(false))
      toastSuccess(response.data.message)

      return { skpId: response.data.skp_id }
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const postCopySKP = createAsyncThunk(
  `${SLICE_NAME}/postCopySKPAPI`,
  async (
    dataParams: { params: PostCreateSKPCopyParamInterface; skpId: number },
    { dispatch, getState },
  ) => {
    dispatch(setIsLoading(true))

    const {
      skpAddNew: { skpCreateList },
    } = getState() as StoreStateType

    try {
      const response = await postCopySKPAPI(dataParams.params, dataParams.skpId)

      const tempSKPLatest = skpCreateList[skpCreateList.length - 1]
      const tempSKPLIst = [...skpCreateList]

      const newSKP = {
        skpId: Number(response.data.skp_id),
      }

      if (tempSKPLatest.skpId === 0) {
        tempSKPLIst.pop()
        tempSKPLIst.push(newSKP)
      }

      dispatch(setSKPCreateList(tempSKPLIst))
      dispatch(setIsLoading(false))
      toastSuccess(response.data.message)

      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const putUpdateSKP = createAsyncThunk(
  `${SLICE_NAME}/putUpdateSKP`,
  async (data: { params: PutUpdateSKPInterface; skpId: number }, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const response = await putUpdateSKPAPI(data.params, data.skpId)
      dispatch(setIsLoading(false))
      dispatch(setEditDetail(false))
      const message = response.data.message || ''
      toastSuccess(message)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const fetchSKPDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchSKPDetail`,
  async (skpId: number, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getSKPDetailAPI(skpId)
      dispatch(setSKPDetails(response.data.data))
      dispatch(setIsLoading(false))
      return response.data.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const fetchVendorDiff = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDiff`,
  async (skpId: number, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getSKPVendorDiffAPI(skpId)
      const { error, ...restData } = response.data
      let canBePassed = true
      if (!error.status) {
        canBePassed = Object.keys(restData).length === 0
        if (!canBePassed) {
          dispatch(setVendorChangedForm(restData))
          dispatch(setShowVendorChangedModal(true))
        }
      }
      dispatch(setIsLoading(false))
      return { canBePassed }
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return { canBePassed: false }
    }
  },
)

export const postSKPFileUpload = createAsyncThunk(
  `${SLICE_NAME}/postSKPFileUpload`,
  async (data: { skpId: number; fileUrl: string }, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await postUploadSKPDetailFileAPI(data.skpId, data.fileUrl)
      dispatch(setIsLoading(false))
      const fileUrl = response.data.file_url || ''
      const message = response.data.message || ''
      if (fileUrl) {
        toastFailed(response.data.message)
        if (fileUrl.includes('http')) {
          const splitUrl = fileUrl.split('/')
          const fileName = splitUrl[splitUrl?.length - 1]
          fetch(fileUrl)
            .then((resFile) => resFile.blob())
            .then((blob) => {
              downloadFileUrl(URL.createObjectURL(blob), fileName)
            })
        }
      } else {
        toastSuccess(message)
      }
      return { isSuccess: !(fileUrl.length > 0) }
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return { isSuccess: false }
    }
  },
)

export const fetchSKPDownloadFile = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDiff`,
  async (skpId: number, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getDownloadSKPDetailsAPI(skpId)
      const { error, file_url } = response.data
      if (!error.status && file_url) {
        const splitUrl = file_url.split('/')
        const fileName = splitUrl[splitUrl?.length - 1]
        downloadFileUrl(file_url, fileName)
      }
      dispatch(setIsLoading(false))
      return true
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const fetchBrandSubmission = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandSubmission`,
  async (brandSubmissionId: number, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getBrandSubmissionDetailAPI(brandSubmissionId)
      const { error, data } = response.data
      if (!error.status) {
        dispatch(setBrandSubmissionsData(data))
      }
      dispatch(setIsLoading(false))
      return false
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const fetchBrandSubmissionsList = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandSubmissionsList`,
  async (params: { name: string }, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getBrandSubmissionsListAPI(params.name)
      const { data } = response.data
      if (data.length > 0) {
        const mappedData = data.map((item) => ({
          id: item.id,
          campaign_theme: item.campaign_theme,
        }))
        dispatch(setBrandSubmissionList(mappedData))
      }
      dispatch(setIsLoading(false))
      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return undefined
    }
  },
)

export const fetchDeckPromotionFile = createAsyncThunk(
  `${SLICE_NAME}/fetchDeckPromotionFile`,
  async (fileUrl: string, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getDeckPromotionFile(fileUrl, 'application/pdf')
      const fileName = fileUrl.match(/[^/]+$/)?.[0] || ''

      if (response) {
        downloadFileBlob({ data: response.data, fileName, mimeType: 'application/pdf' })
      }
      dispatch(setIsLoading(false))
      return true
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const deleteSKP = createAsyncThunk(
  `${SLICE_NAME}/deleteSKP`,
  async (skpId: string, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      await deleteSKPAPI(skpId)
      toastSuccess('SKP deleted successfully')
      return true
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)

export const fetchBrandV2Option = createAsyncThunk(
  `${SLICE_NAME}/deleteSKP`,
  async ({ name }: { name: string }, { dispatch }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getAllBrandV2({ pageSize: 50, pageIndex: 1, name })
      dispatch(
        setBrandV2Options(
          response.data.data.map((brand) => ({
            brand_name: brand.brand_name,
            brand_id: brand.brand_id,
          })),
        ),
      )
      dispatch(setIsLoading(false))

      return { brand: response.data }
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return false
    }
  },
)
